import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { highlightText } from '@/features/shared/lib/utils';

export default function JournalCard({ item, linkMode = 'path', highlightQuery = '' }) {
  const navigate = useNavigate();
  const id = item?.journal_id ?? item?.id;

  const to = linkMode === 'query' ? `/journals/?id=${encodeURIComponent(id)}` : `/journals/${encodeURIComponent(id)}`;

  const onCardClick = useCallback((e) => {
    if (!id) return;

    // Check for Cmd (Mac) or Ctrl (Windows) key to open in a new tab
    if (e && (e.metaKey || e.ctrlKey)) {
      let homeUrl = (typeof rmVars !== 'undefined' && rmVars.homeUrl) ? rmVars.homeUrl : '';
      // If we are on port 3000 (dev mode), ensure homeUrl also uses port 3000
      if (typeof window !== 'undefined' && window.location.port === '3000' && homeUrl && !homeUrl.includes(':3000')) {
        homeUrl = homeUrl.replace(/(\/\/[^\/]+)/, '$1:3000');
      }
      const newTabUrl = `${homeUrl}/journals/${encodeURIComponent(id)}`;
      window.open(newTabUrl, '_blank');
      return;
    }

    // prevent default anchor/button behavior to avoid page jump or reload
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    // push navigation (default behavior of navigate)
    navigate(to);
    // ensure scroll to top on route change
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        // slight delay to allow route transition, then scroll
        setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }), 0);
      }
    } catch (e) {
      // no-op
    }
  }, [id, to, navigate]);

  return (
    <li className="p-6 bg-white border border-[#E4E8F1] rounded-xl lg:p-8 hover:shadow-[0px_4px_20px_rgba(0,0,0,0.1)] transition durarion-300">
      <div className="flex flex-col">
        <div className="order-3 flex flex-col items-start text-[#6C7687] min-[800px]:flex-row flex-wrap min-[800px]:order-1 min-[800px]:gap-[6px] min-[800px]:mb-3">
          <p className="text-[14px] text-[#B4BCCC] font-medium mb-2 min-[800px]:hidden"> Journal Ranking </p>
          {Array.isArray(item?.journal_ranking) && item.journal_ranking.map((rk, idx) => {
            const type = rk?.name || rk?.indicator_type || '';
            const year = rk?.year || rk?.indicator_year || '';
            const pctRaw = rk?.percent;
            let pctText = '';
            if (typeof pctRaw === 'number') {
              pctText = pctRaw.toFixed(pctRaw < 1 ? 2 : 2).replace(/\.00$/, '');
            } else if (typeof pctRaw === 'string') {
              pctText = pctRaw;
            }
            const cls = String(type).toLowerCase();
            return (<span key={idx} className={`type-${cls} mb-[6px] bg-[#F3F5F9] px-[8px] py-[6px] text-[13px] rounded`}>
              {type} {year} <strong>Top {pctText}% / Q{rk?.quartile}</strong>
            </span>);
          })}
        </div>
        <div className="order-1 text-[18px] font-bold leading-[130%] mb-3 min-[800px]:order-2 min-[800px]:mb-[10px] flex items-center gap-2 cursor-pointer hover:underline underline-offset-2 decoration-1"  onClick={onCardClick}>
          <span>{highlightQuery ? highlightText(item.journal_title, highlightQuery) : item.journal_title}</span>
          {typeof rmVars !== 'undefined' && rmVars?.isEditor && id && (
            <a
              href={`${rmVars.homeUrl}/wp-admin/admin.php?page=journal&record_id=${id}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 ml-2"
              target="_blank"
              rel="noopener noreferrer"
              title="Edit Journal"
            >
              <span
                className="editor-edit"
                style={{
                  width: '18px',
                  height: '18px',
                  filter: 'invert(11%) sepia(70%) saturate(6625%) hue-rotate(348deg) brightness(88%) contrast(99%)',
                  backgroundSize: '18px auto',
                  display: 'inline-block',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center center',
                  backgroundImage: `url(${rmVars.themeUrl}images/edit-regular.svg)`
                }}
              />
            </a>
          )}
        </div>
        <div className="order-2 flex flex-wrap gap-x-4 gap-y-2 text-[14px] text-[#6C7687] font-semibold leading-[100%] pb-4 border-b border-b-[#E4E8F1] lg:order-3 lg:border-none lg:pb-0">
          {(() => {
            const issn = item?.issn;
            const eissn = item?.eissn;
            const publisher = item?.publisher;
            const hasIssn = issn !== undefined && issn !== null && String(issn).trim() !== '';
            const hasEissn = eissn !== undefined && eissn !== null && String(eissn).trim() !== '';
            const hasPublisher = publisher !== undefined && publisher !== null && String(publisher).trim() !== '';

            const hasRegisteredDb = Array.isArray(item?.registered_db) && item.registered_db.length > 0 && item.registered_db.some(r => r?.name);
            const hasResourceCount = (item.resource_count ?? 0) > 0;

            const infoItems = [
              hasIssn && { type: 'text', label: 'ISSN', value: issn },
              hasEissn && { type: 'text', label: 'eISSN', value: eissn },
              hasPublisher && { type: 'text', label: 'Publisher', value: publisher },
              hasRegisteredDb && { type: 'db', label: 'Registered DB', value: item.registered_db },
              item.resource_count > 0 && { type: 'articles', label: 'PNU Articles', value: item.resource_count },
            ].filter(Boolean);

            return infoItems.map((info, idx) => (
              <React.Fragment key={idx}>
                {info.type === 'text' && (
                  <div>
                    <span className="text-[#B4BCCC] font-medium mr-[5px]">{info.label}</span>
                    <span>{String(info.value)}</span>
                  </div>
                )}
                {info.type === 'db' && (
                  <div className="flex items-center gap-[4px]">
                    <span className="text-[14px] text-[#B4BCCC] font-medium mr-[5px]">{info.label}</span>
                    {info.value.map((r, dbIdx) => {
                      if (!r?.name) return null;
                      const nameStr = String(r.name).toLowerCase();
                      const type = nameStr.includes('kci') ? 'kci' : nameStr;
                      return (
                        <span key={dbIdx} className={`regist-tpye-${type} px-[5px] py-[2px] rounded text-[13px]`}>
                          {r.name}
                        </span>
                      );
                    })}
                  </div>
                )}
                {info.type === 'articles' && (
                  <div className="flex flex-wrap items-center gap-y-[10px] gap-x-[2px] text-[14px]">
                    <a
                      href={`/publications/?journal_id[]=${encodeURIComponent(id)}`}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full text-[13px] text-[#B4BCCC] underline underline-offset-2 font-medium lg:w-auto"
                    >
                      PNU Articles (<span>{info.value}</span>)
                    </a>
                  </div>
                )}
                {idx < infoItems.length - 1 && (
                  <span className="hidden w-[1px] h-[10px] bg-[#B4BCCC] translate-y-[4px] min-[800px]:inline-block"></span>
                )}
              </React.Fragment>
            ));
          })()}
        </div>
        <span></span>
        <div className="hidden mt-4 lg:mb-6 text-[#6C7687] text-[12px] lg:flex lg:order-3 flex-col gap-y-1">
          {(() => {
            const arr = (v) => Array.isArray(v) ? v : (v ? [v] : []);
            const jcr = arr(item?.jcr_category);
            const scopus = arr(item?.scopus_category);
            const kci = arr(item?.kci_category);
            const hasAny = jcr.length || scopus.length || kci.length;
            if (!hasAny) return null;
            return (<>
              {jcr.length > 0 && (
                <ul className="flex flex-wrap gap-1">
                  {jcr.map((c, idx) => (
                    <li key={`jcr-${idx}`} className="px-[10px] py-[6px] bg-[#F3F5F9] rounded-full">
                      <strong>WOS</strong> - <span>{c?.category_name || ''}</span>
                    </li>))}
                </ul>
              )}
              {scopus.length > 0 && (
                <ul className="flex flex-wrap gap-1 lg:flex-row">
                  {scopus.map((c, idx) => (
                    <li key={`scopus-${idx}`} className="px-[10px] py-[6px] bg-[#F3F5F9] rounded-full">
                      <strong>Scopus</strong> - <span>{c?.category_name || ''} > {c?.sub_category_name || ''}</span>
                    </li>
                  ))}
                </ul>
              )}
              {kci.length > 0 && (
                <ul className="flex flex-wrap gap-1 lg:flex-row">
                  {kci.filter(c => c?.sub_category_id).map((c, idx) => (
                    <li key={`kci-${idx}`} className="px-[10px] py-[6px] bg-[#F3F5F9] rounded-full">
                      <strong>KCI</strong> - <span>{c?.category_name || ''} > {c?.sub_category_name || ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>);
          })()}
        </div>
      </div>
      <div className="order-10 hidden text-[#828293] text-[13px] font-semibold text-center mt-[52px]">
        <button className="inline-flex items-center justify-center gap-1 cursor-pointer">
          <img
            src={`${rmVars.themeUrl}images/expand_more_8D9AAF.png`}
            className="w-[14px] transition-transform duration-200" alt="show" />Show All Categories
        </button>
      </div>
      <div className="order-10 hidden text-[#828293] text-[13px] font-semibold text-center mt-[52px]">
        <button className="inline-flex items-center justify-center gap-1 cursor-pointer"><img
          src={`${rmVars.themeUrl}images/expand_more_8D9AAF.png`}
          className="w-[14px] rotate-180 transition-transform duration-200" alt="show" />Hide Categories
        </button>
      </div>
    </li>
  );
}
